import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Leaf, Globe } from "lucide-react";

export default function Header() {
  const { t, i18n } = useTranslation();
  const { lang = "fr" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const base = `/${lang}`;

  // Build path swap for language switcher: preserve current page, only change /fr <-> /en
  const swapLang = (target) => {
    const parts = location.pathname.split("/").filter(Boolean);
    if (parts[0] === "fr" || parts[0] === "en") {
      parts[0] = target;
    } else {
      parts.unshift(target);
    }
    return "/" + parts.join("/");
  };

  const changeLanguage = (target) => {
    if (target === lang) return;
    i18n.changeLanguage(target);
    navigate(swapLang(target), { replace: false, state: { langSwitch: true } });
    setOpen(false);
  };

  const navItems = [
    { to: `${base}`, label: t("nav.home"), end: true, id: "nav-home" },
    { to: `${base}#why`, label: t("nav.opportunities"), id: "nav-opportunities" },
    { to: `${base}#how`, label: t("nav.how_it_works"), id: "nav-how" },
    { to: `${base}#transparency`, label: t("nav.transparency"), id: "nav-transparency" },
  ];

  const handleAnchorClick = (e, to) => {
    if (!to.includes("#")) return;
    const [path, hash] = to.split("#");
    e.preventDefault();
    setOpen(false);
    if (location.pathname === path) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      // Navigate to home then scroll to the target section
      navigate(path);
      // Wait for route change to render, then scroll
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    }
  };

  return (
    <header className="vx-header sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to={base}
            className="flex items-center gap-2"
            data-testid="header-logo"
            onClick={() => setOpen(false)}
          >
            <span
              className="inline-flex items-center justify-center w-8 h-8"
              style={{ backgroundColor: "var(--vx-emerald)" }}
            >
              <Leaf size={18} color="white" strokeWidth={2.2} />
            </span>
            <span className="font-serif-display text-xl font-bold" style={{ color: "var(--vx-navy)" }}>
              {t("brand.name")}
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" aria-label="primary">
            {navItems.map((item) => (
              <NavLink
                key={item.id}
                to={item.to}
                end={item.end}
                data-testid={item.id}
                onClick={(e) => handleAnchorClick(e, item.to)}
                className={({ isActive }) =>
                  `text-sm font-medium ${isActive && !item.to.includes("#") ? "text-emerald-700" : ""}`
                }
                style={{ color: "var(--vx-graphite)" }}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Right side: Language + CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <div className="flex items-center border" style={{ borderColor: "var(--vx-border)" }} data-testid="language-switcher">
              <button
                type="button"
                onClick={() => changeLanguage("fr")}
                data-testid="lang-switch-fr"
                aria-label={t("language.switch_to_fr")}
                className={`px-3 py-1 text-xs font-mono-data uppercase tracking-wider ${lang === "fr" ? "text-white" : ""}`}
                style={{ backgroundColor: lang === "fr" ? "var(--vx-navy)" : "transparent", color: lang === "fr" ? "white" : "var(--vx-graphite)" }}
              >
                FR
              </button>
              <button
                type="button"
                onClick={() => changeLanguage("en")}
                data-testid="lang-switch-en"
                aria-label={t("language.switch_to_en")}
                className={`px-3 py-1 text-xs font-mono-data uppercase tracking-wider ${lang === "en" ? "text-white" : ""}`}
                style={{ backgroundColor: lang === "en" ? "var(--vx-navy)" : "transparent", color: lang === "en" ? "white" : "var(--vx-graphite)" }}
              >
                EN
              </button>
            </div>
            <a
              href={`${base}#newsletter`}
              onClick={(e) => handleAnchorClick(e, `${base}#newsletter`)}
              className="vx-btn-primary"
              data-testid="header-cta-newsletter"
            >
              {t("cta.primary")}
            </a>
          </div>

          {/* Mobile burger */}
          <button
            type="button"
            className="lg:hidden p-2"
            onClick={() => setOpen(!open)}
            aria-label={open ? t("nav.menu_close") : t("nav.menu_open")}
            data-testid="mobile-menu-toggle"
            style={{ color: "var(--vx-navy)" }}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden vx-header border-t" style={{ borderColor: "var(--vx-border)" }} data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-4">
            {navItems.map((item) => (
              <NavLink
                key={item.id}
                to={item.to}
                end={item.end}
                data-testid={`m-${item.id}`}
                onClick={(e) => {
                  handleAnchorClick(e, item.to);
                  if (!item.to.includes("#")) setOpen(false);
                }}
                className="text-base font-medium"
                style={{ color: "var(--vx-graphite)" }}
              >
                {item.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-3 pt-3 border-t" style={{ borderColor: "var(--vx-border)" }}>
              <Globe size={16} color="var(--vx-graphite)" />
              <button
                type="button"
                onClick={() => changeLanguage("fr")}
                data-testid="m-lang-switch-fr"
                className="text-xs font-mono-data uppercase tracking-wider px-3 py-1"
                style={{ backgroundColor: lang === "fr" ? "var(--vx-navy)" : "transparent", color: lang === "fr" ? "white" : "var(--vx-graphite)", border: "1px solid var(--vx-border)" }}
              >
                FR
              </button>
              <button
                type="button"
                onClick={() => changeLanguage("en")}
                data-testid="m-lang-switch-en"
                className="text-xs font-mono-data uppercase tracking-wider px-3 py-1"
                style={{ backgroundColor: lang === "en" ? "var(--vx-navy)" : "transparent", color: lang === "en" ? "white" : "var(--vx-graphite)", border: "1px solid var(--vx-border)" }}
              >
                EN
              </button>
            </div>
            <a
              href={`${base}#newsletter`}
              onClick={(e) => handleAnchorClick(e, `${base}#newsletter`)}
              className="vx-btn-primary justify-center"
              data-testid="m-header-cta-newsletter"
            >
              {t("cta.primary")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
